import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Tab,
  Tabs,
  Tooltip,
} from '@mui/material';
import { Ban, CheckCircle2, Code2, KeyRound, Power, ShieldOff, X } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import {
  useRevokeUserApiKey,
  useUpdateUserEmbedWidgetStatus,
  useUserCredentials,
} from '../hooks/useUsers';
import type { AdminUserApiKey, AdminUserEmbedWidget, User } from '../types';
import { useNotification } from '../../../../hooks/useNotification';
import { formatAdminNumber } from '../../../../utils/numberFormat';

interface UserCredentialsModalProps {
  open: boolean;
  user: User | null;
  initialTab?: CredentialTab;
  onClose: () => void;
}

type CredentialTab = 'apiKeys' | 'widgets';

const formatDateTime = (value: string | null) => {
  if (!value) return 'Never';
  return new Intl.DateTimeFormat('en-US', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));
};

const statusClassName = (status: string) => {
  const normalized = status.toLowerCase();
  if (normalized === 'active') {
    return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300';
  }
  if (normalized === 'revoked') {
    return 'bg-red-100 text-red-700 dark:bg-red-500/10 dark:text-red-300';
  }
  return 'bg-amber-100 text-amber-700 dark:bg-amber-500/10 dark:text-amber-300';
};

const MetadataLine: React.FC<{ label: string; value: string | number | null | undefined }> = ({
  label,
  value,
}) => (
  <div className="min-w-0">
    <p className="text-xs font-medium uppercase tracking-wide text-gray-400 dark:text-slate-500">
      {label}
    </p>
    <p className="mt-1 truncate text-sm text-gray-700 dark:text-slate-200">{value ?? 'N/A'}</p>
  </div>
);

export const UserCredentialsModal: React.FC<UserCredentialsModalProps> = ({
  open,
  user,
  initialTab = 'apiKeys',
  onClose,
}) => {
  const [tab, setTab] = useState<CredentialTab>(initialTab);
  const { success, error: notifyError } = useNotification();
  const credentialsQuery = useUserCredentials(open ? user?.publicId : undefined);
  const revokeApiKeyMutation = useRevokeUserApiKey();
  const updateWidgetStatusMutation = useUpdateUserEmbedWidgetStatus();

  useEffect(() => {
    if (open) {
      setTab(initialTab);
    }
  }, [initialTab, open]);

  const handleRevokeApiKey = async (apiKey: AdminUserApiKey) => {
    if (!user || apiKey.status === 'REVOKED') return;
    if (!window.confirm(`Revoke API key ${apiKey.prefix || apiKey.publicId}?`)) return;

    try {
      await revokeApiKeyMutation.mutateAsync({
        publicId: user.publicId,
        credentialPublicId: apiKey.publicId,
      });
      success('API key revoked successfully.');
    } catch (error) {
      notifyError(error instanceof Error ? error.message : 'Failed to revoke API key.');
    }
  };

  const handleWidgetStatus = async (
    widget: AdminUserEmbedWidget,
    status: 'active' | 'disabled' | 'revoked'
  ) => {
    if (!user || widget.status === status) return;
    if (!window.confirm(`${status === 'active' ? 'Activate' : status} widget ${widget.name}?`)) {
      return;
    }

    try {
      await updateWidgetStatusMutation.mutateAsync({
        publicId: user.publicId,
        widgetPublicId: widget.publicId,
        status,
      });
      success('Embed widget status updated successfully.');
    } catch (error) {
      notifyError(error instanceof Error ? error.message : 'Failed to update embed widget.');
    }
  };

  const data = credentialsQuery.data;
  const displayName = user?.userName || user?.email || 'User';
  const isMutating = revokeApiKeyMutation.isPending || updateWidgetStatusMutation.isPending;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: {
          bgcolor: (theme) => (theme.palette.mode === 'dark' ? 'rgb(15, 23, 42)' : 'white'),
          color: (theme) => (theme.palette.mode === 'dark' ? 'white' : 'rgb(17, 24, 39)'),
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <div className="flex min-w-0 items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="truncate text-lg font-semibold text-gray-900 dark:text-white">
              {displayName}
            </p>
            <p className="truncate text-sm font-normal text-gray-500 dark:text-slate-400">
              Credential metadata
            </p>
          </div>
          <IconButton
            aria-label="Close credential metadata"
            onClick={onClose}
            sx={{
              color: (theme) =>
                theme.palette.mode === 'dark' ? 'rgb(148, 163, 184)' : 'rgb(107, 114, 128)',
            }}
          >
            <X size={20} />
          </IconButton>
        </div>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {credentialsQuery.isLoading ? (
          <div className="flex min-h-80 items-center justify-center">
            <CircularProgress sx={{ color: '#3B82F6' }} />
          </div>
        ) : credentialsQuery.isError ? (
          <Alert severity="error">
            {credentialsQuery.error?.message || 'Unable to load credential metadata.'}
          </Alert>
        ) : data ? (
          <div className="space-y-5">
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <SummaryMetric
                Icon={KeyRound}
                label="Active API keys"
                value={`${formatAdminNumber(data.summary.activeApiKeyCount)}/${formatAdminNumber(
                  data.summary.apiKeyCount
                )}`}
                colorClass="text-blue-500"
              />
              <SummaryMetric
                Icon={Code2}
                label="Active widgets"
                value={`${formatAdminNumber(
                  data.summary.activeEmbedWidgetCount
                )}/${formatAdminNumber(data.summary.embedWidgetCount)}`}
                colorClass="text-teal-500"
              />
            </div>

            <Tabs
              value={tab}
              onChange={(_, value: CredentialTab) => setTab(value)}
              aria-label="Credential type"
            >
              <Tab icon={<KeyRound size={16} />} iconPosition="start" label="API Keys" value="apiKeys" />
              <Tab icon={<Code2 size={16} />} iconPosition="start" label="Widgets" value="widgets" />
            </Tabs>

            {tab === 'apiKeys' ? (
              <div className="space-y-3">
                {data.apiKeys.length === 0 ? (
                  <EmptyState label="No API keys" />
                ) : (
                  data.apiKeys.map((apiKey) => (
                    <ApiKeyRow
                      key={apiKey.publicId}
                      apiKey={apiKey}
                      loading={isMutating}
                      onRevoke={() => handleRevokeApiKey(apiKey)}
                    />
                  ))
                )}
              </div>
            ) : (
              <div className="space-y-3">
                {data.embedWidgets.length === 0 ? (
                  <EmptyState label="No embed widgets" />
                ) : (
                  data.embedWidgets.map((widget) => (
                    <WidgetRow
                      key={widget.publicId}
                      widget={widget}
                      loading={isMutating}
                      onActivate={() => handleWidgetStatus(widget, 'active')}
                      onDisable={() => handleWidgetStatus(widget, 'disabled')}
                      onRevoke={() => handleWidgetStatus(widget, 'revoked')}
                    />
                  ))
                )}
              </div>
            )}
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

const SummaryMetric: React.FC<{
  Icon: LucideIcon;
  label: string;
  value: string;
  colorClass: string;
}> = ({ Icon, label, value, colorClass }) => (
  <div className="rounded-lg border border-gray-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-800">
    <div className="flex items-center gap-2 text-sm font-medium text-gray-500 dark:text-slate-400">
      <Icon size={17} className={colorClass} />
      {label}
    </div>
    <div className="mt-2 text-2xl font-bold text-gray-900 dark:text-white">{value}</div>
  </div>
);

const EmptyState: React.FC<{ label: string }> = ({ label }) => (
  <div className="rounded-lg border border-dashed border-gray-300 p-8 text-center text-sm text-gray-500 dark:border-slate-700 dark:text-slate-400">
    {label}
  </div>
);

const ApiKeyRow: React.FC<{
  apiKey: AdminUserApiKey;
  loading: boolean;
  onRevoke: () => void;
}> = ({ apiKey, loading, onRevoke }) => {
  const metadataName =
    typeof apiKey.metadata?.appName === 'string' ? apiKey.metadata.appName : 'API key';

  return (
    <div className="rounded-lg border border-gray-200 p-4 dark:border-slate-700">
      <div className="flex min-w-0 flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <p className="truncate text-sm font-semibold text-gray-900 dark:text-white">
              {metadataName}
            </p>
            <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${statusClassName(apiKey.status)}`}>
              {apiKey.status}
            </span>
          </div>
          <p className="mt-1 truncate font-mono text-xs text-gray-500 dark:text-slate-400">
            {apiKey.prefix || apiKey.publicId}
          </p>
        </div>

        <Tooltip title="Revoke API key">
          <span>
            <Button
              size="small"
              color="error"
              variant="outlined"
              startIcon={<ShieldOff size={16} />}
              disabled={loading || apiKey.status === 'REVOKED'}
              onClick={onRevoke}
            >
              Revoke
            </Button>
          </span>
        </Tooltip>
      </div>

      <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <MetadataLine label="Environment" value={apiKey.environment || 'live'} />
        <MetadataLine label="Scopes" value={apiKey.scopes.length || 'None'} />
        <MetadataLine label="Last used" value={formatDateTime(apiKey.lastUsedAt)} />
        <MetadataLine label="Created" value={formatDateTime(apiKey.createdAt)} />
      </div>
    </div>
  );
};

const WidgetRow: React.FC<{
  widget: AdminUserEmbedWidget;
  loading: boolean;
  onActivate: () => void;
  onDisable: () => void;
  onRevoke: () => void;
}> = ({ widget, loading, onActivate, onDisable, onRevoke }) => (
  <div className="rounded-lg border border-gray-200 p-4 dark:border-slate-700">
    <div className="flex min-w-0 flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
      <div className="min-w-0">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <p className="truncate text-sm font-semibold text-gray-900 dark:text-white">
            {widget.name}
          </p>
          <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${statusClassName(widget.status)}`}>
            {widget.status}
          </span>
        </div>
        <p className="mt-1 truncate text-xs text-gray-500 dark:text-slate-400">
          {widget.agentName} · {widget.publicKeyPrefix || widget.publicId}
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        <Tooltip title="Activate widget">
          <span>
            <Button
              size="small"
              variant="outlined"
              startIcon={<CheckCircle2 size={16} />}
              disabled={loading || widget.status === 'active'}
              onClick={onActivate}
            >
              Activate
            </Button>
          </span>
        </Tooltip>
        <Tooltip title="Disable widget">
          <span>
            <Button
              size="small"
              color="warning"
              variant="outlined"
              startIcon={<Power size={16} />}
              disabled={loading || widget.status === 'disabled'}
              onClick={onDisable}
            >
              Disable
            </Button>
          </span>
        </Tooltip>
        <Tooltip title="Revoke widget">
          <span>
            <Button
              size="small"
              color="error"
              variant="outlined"
              startIcon={<Ban size={16} />}
              disabled={loading || widget.status === 'revoked'}
              onClick={onRevoke}
            >
              Revoke
            </Button>
          </span>
        </Tooltip>
      </div>
    </div>

    <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      <MetadataLine label="Environment" value={widget.environment} />
      <MetadataLine label="Origins" value={widget.allowedOrigins.length || 'None'} />
      <MetadataLine label="Session limit" value={widget.maxSessionsPerDay} />
      <MetadataLine label="Message limit" value={widget.maxMessagesPerSession} />
      <MetadataLine label="Last used" value={formatDateTime(widget.lastUsedAt)} />
      <MetadataLine label="Created" value={formatDateTime(widget.createdAt)} />
    </div>
  </div>
);
